/*****************************************************************************
 * Copyright(c) 2025 Qorus Inc
 * All rights reserved
 *****************************************************************************
 * NAME: src/utils/tokenManager.ts
 * DEVELOPER: Jaime Rivera
 * DESCRIPTION: Centralized token and authentication management
 * REVISIONS:
 * Date Change      ID              Author              Description
 * -----------      -----------     --------------      ------------------------------------
 * 20-Nov-2025      HOTFIX-PKCE    Jaime Rivera        Initial implementation
 *****************************************************************************/

import { authStorage, type StoredAuthData } from './authStorage';
import { createSession, exchangeToken } from '../hooks/useAuth';
import { generatePKCEConfig } from './pkceUtils';
 
 class TokenManager {
   private static instance: TokenManager;
   private isRefreshing = false;
   private refreshPromise: Promise<string> | null = null;
   private refreshCallbacks: Array<(token: string) => void> = [];
   private embedId: string | null = null;
 
   static getInstance(): TokenManager {
     if (!TokenManager.instance) {
       TokenManager.instance = new TokenManager();
     }
     return TokenManager.instance;
   }
 
   isTokenValid(): boolean {
     return authStorage.hasValidAuthData();
   }
 
   async getValidToken(): Promise<string> {
     if (this.isTokenValid()) {
       const authData = authStorage.getAuthData();
       return authData!.accessToken;
     }
 
     if (this.isRefreshing && this.refreshPromise) {
       return this.refreshPromise;
     }
 
     return this.refreshToken();
   }
 
   private async refreshToken(): Promise<string> {
     this.isRefreshing = true;
     this.refreshPromise = this.performTokenRefresh();
     
     try {
       const newToken = await this.refreshPromise;
       this.refreshCallbacks.forEach(callback => callback(newToken));
       this.refreshCallbacks = [];
       return newToken;
     } catch (error) {
       this.clearToken();
       throw error;
     } finally {
       this.isRefreshing = false;
       this.refreshPromise = null;
     }
   }
 
   private async performTokenRefresh(): Promise<string> {
     try {
       let pkceData = authStorage.getPKCEData();
       
       if (!pkceData) {
         const embedId = this.getEmbedId();
         const pkceConfig = await generatePKCEConfig();
         pkceData = {
           ...pkceConfig,
           embedId
         };
         authStorage.setPKCEData(pkceData);
       }
 
       const sessionResponse = await createSession(
         pkceData.embedId,
         pkceData.codeChallenge
       );
       
       authStorage.setSessionId(sessionResponse.sessionId);
 
       const tokenResponse = await exchangeToken(
         sessionResponse.sessionId,
         pkceData.codeVerifier
       );
       
       const expiresInMinutes = parseInt(tokenResponse.expiresIn.replace('m', ''));
       const expiresInMs = expiresInMinutes * 60 * 1000;
       
       const authData: StoredAuthData = {
         accessToken: tokenResponse.accessToken,
         tokenExpiry: Date.now() + expiresInMs,
         tokenType: tokenResponse.tokenType,
         sessionId: sessionResponse.sessionId,
         codeVerifier: pkceData.codeVerifier,
         embedId: pkceData.embedId
       };
       
       authStorage.setAuthData(authData);
       return tokenResponse.accessToken;
       
     } catch (error) {
       throw error;
     }
   }
 
   getCurrentToken(): string | null {
     const authData = authStorage.getAuthData();
     return authData?.accessToken || null;
   }
 
   setToken(token: string, expiresIn: number, tokenType: string = 'Bearer'): void {
     const authData: StoredAuthData = {
       accessToken: token,
       tokenExpiry: Date.now() + (expiresIn * 60 * 1000),
       tokenType,
       sessionId: authStorage.getSessionId() || '',
       codeVerifier: authStorage.getPKCEData()?.codeVerifier || '',
       embedId: authStorage.getPKCEData()?.embedId || ''
     };
     
     authStorage.setAuthData(authData);
   }
 
   clearToken(): void {
     authStorage.clearAll();
   }
 
  requiresAuth(url?: string): boolean {
    if (!url) {
      return false;
    }
    
    return url.includes('/api/hq-nd');
  }
 
   onTokenRefresh(callback: (token: string) => void): void {
     this.refreshCallbacks.push(callback);
   }
 
   setEmbedId(embedId: string): void {
     this.embedId = embedId;
   }
 
   private getEmbedId(): string {
     if (this.embedId) {
       return this.embedId;
     }
     
     if (typeof window !== 'undefined' && (window as any).appointmentWidgetConfig?.embedId) {
       return (window as any).appointmentWidgetConfig.embedId;
     }
     
     if (import.meta.env.VITE_EMBED_ID) {
       return import.meta.env.VITE_EMBED_ID;
     }
     
     return 'default';
   }
 
   getTokenInfo(): { isValid: boolean; expiresAt?: number; timeUntilExpiry?: number } {
     const authData = authStorage.getAuthData();
     
     if (!authData) {
       return { isValid: false };
     }
     
     const now = Date.now();
     const isValid = now < authData.tokenExpiry;
     const timeUntilExpiry = authData.tokenExpiry - now;
     
     return {
       isValid,
       expiresAt: authData.tokenExpiry,
       timeUntilExpiry: isValid ? timeUntilExpiry : 0
     };
   }
 }
 
 export const tokenManager = TokenManager.getInstance();
 export default tokenManager;